import React, { useEffect, useState } from "react";
import { ReportShell } from "./ReportShell.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getDataCoverage } from "../../data/reportsClient.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiPopover, HOME_KPI_TOOLTIP_STYLE } from "../../components/KpiPopover.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { T } from "../../app/tokens.js";
import { FeedTable, GapList, MonthBars, OfficeMatrix, SectionHead, cardStyle, periodNote } from "./dataCoverage/CoverageParts.jsx";
import { OfficeDayDrilldown } from "./dataCoverage/OfficeDayDrilldown.jsx";
import { fmtCount, fmtDate, fmtDateTime, fmtMonth, fmtRatio } from "./dataCoverage/coverageFormat.js";
import "./dataCoverage/coverageControls.css";

const REPORT_KEY = "report-data-coverage";
const body = { maxWidth: 1200, minWidth: 400, margin: "0 auto", padding: "0 16px 40px", boxSizing: "border-box" };

function Notice({ testId, role = "status", children }) {
  return <div data-testid={testId} role={role} style={{ ...cardStyle, padding: 24, color: T.mid, fontSize: T.fsBase }}>{children}</div>;
}

function LoadingBody() {
  return (
    <div data-testid="data-coverage-loading" role="status" aria-live="polite" aria-busy="true">
      <span style={{ position: "absolute", left: -9999 }}>Loading data coverage…</span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12, marginBottom: 16 }}>
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} style={{ height: KPI_TILE_ROW_HEIGHT, borderRadius: 14, background: T.card, border: `1px solid ${T.border}` }} />)}
      </div>
      <div style={{ ...cardStyle, padding: 16 }}>
        {[0, 1, 2, 3, 4].map((i) => <div key={i} style={{ height: 34, borderRadius: 6, background: T.lightGray, marginBottom: 8 }} />)}
      </div>
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  const opts = [["feed", "By feed"], ["office", "By office"]];
  return (
    <div role="group" aria-label="Coverage view" style={{ display: "inline-flex", background: T.lightGray, borderRadius: 9, padding: 3 }}>
      {opts.map(([key, label]) => (
        <button key={key} type="button" data-testid={`toggle-view-${key}`} aria-pressed={view === key} onClick={() => onChange(key)}
          style={{
            border: "none", borderRadius: 7, padding: "6px 14px", fontSize: T.fsSm5, cursor: "pointer",
            fontWeight: view === key ? T.fwBold : T.fwSemibold, color: view === key ? T.dark : T.tabInactiveFg,
            background: view === key ? T.card : "transparent", boxShadow: view === key ? T.shadowSegment : "none",
          }}>{label}</button>
      ))}
    </div>
  );
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const MIN_MONTH = "2020-01";

function DeepDive({ d, threshold, selected, onSelectMonth, onOpen }) {
  const p = d.portfolio ?? {};
  return (
    <section aria-labelledby="dc-badging" data-testid="section-badging-deep-dive" style={cardStyle}>
      <SectionHead id="dc-badging" title="Badging coverage"
        right={`${fmtMonth(selected)} · ${periodNote(d.periodState, p.isComplete)}`} />
      <div data-testid="text-portfolio-breakdown" style={{ display: "flex", flexWrap: "wrap", gap: "4px 20px", padding: "0 16px 10px", fontSize: T.fsSm, color: T.mid }}>
        <span>Coverage <b style={{ color: T.dark }}>{fmtRatio(p.coverageRatio)}</b></span>
        <span>Observed <b style={{ color: T.dark }}>{fmtCount(p.observedDays)}</b></span>
        <span>Eligible <b style={{ color: T.dark }}>{fmtCount(p.eligibleDays)}</b></span>
        <span>Missing <b style={{ color: p.missingDays > 0 ? T.ragAmberText : T.dark }}>{fmtCount(p.missingDays)}</b></span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20, padding: "0 16px 14px" }}>
        <div>
          <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid, letterSpacing: ".03em", textTransform: "uppercase" }}>Coverage by month</div>
          <MonthBars months={d.months} selectedMonth={selected} threshold={threshold} onSelect={onSelectMonth} />
        </div>
        <div>
          <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid, letterSpacing: ".03em", textTransform: "uppercase" }}>
            {fmtMonth(selected)} office gaps · {fmtCount(d.gaps?.length ?? 0)}
          </div>
          <GapList gaps={d.gaps} onOpen={onOpen} />
        </div>
      </div>
      <p style={{ margin: "0 16px 16px", padding: "10px 12px", background: T.hoverBg, borderRadius: 9, color: T.mid, fontSize: T.fsSm }}>
        Eligible days exclude the current and future days, weekends, holidays, days outside an office's lifecycle, and days with no positive headcount. Elapsed months are final as measured, not complete by calendar.
      </p>
    </section>
  );
}

function CoverageBody({ initialMonth }) {
  const [month, setMonth] = useState(initialMonth ?? null);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [view, setView] = useState("feed");
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    let live = true;
    setState((s) => ({ status: "loading", data: s.data, error: null }));
    getDataCoverage(month ? { month } : {})
      .then((data) => { if (live) setState({ status: "ready", data, error: null }); })
      .catch((error) => { if (live) setState({ status: "error", data: null, error }); });
    return () => { live = false; };
  }, [month, attempt]);

  if (state.status === "error") {
    return (
      <div role="alert" data-testid="data-coverage-error" style={{ ...cardStyle, padding: 20, background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, color: T.ragRed, fontSize: T.fsBase }}>
        <span data-testid="text-coverage-error">{state.error?.message || "Data coverage could not be loaded."}</span>{" "}
        <button type="button" data-testid="button-coverage-retry" onClick={() => setAttempt((n) => n + 1)}
          style={{ background: "none", border: "none", color: T.accent, fontWeight: T.fwSemibold, cursor: "pointer", padding: 0, fontSize: T.fsBase }}>Retry</button>
      </div>
    );
  }
  // Hide prior month's content entirely while a month loads so stale rows cannot open the wrong detail.
  if (!state.data || state.status === "loading") return <LoadingBody />;

  const d = state.data;
  const k = d.kpis ?? {};
  const threshold = d.completenessThreshold ?? 0.95;
  const selected = d.selectedMonth;
  const maxMonth = typeof d.asOf === "string" ? d.asOf.slice(0, 7) : selected;
  const partialIssues = k.issues;
  const p = d.portfolio ?? {};
  const selectMonth = (m) => {
    if (!MONTH_RE.test(m ?? "") || m < MIN_MONTH || (maxMonth && m > maxMonth)) return;
    setDrill(null);
    if (m !== selected) setMonth(m);
  };
  const badgingNull = k.badgingCoverageRatio === null || k.badgingCoverageRatio === undefined;
  const explain = (text, source = "Persisted feed evidence") => ({
    tooltip: <KpiPopover period={fmtMonth(selected)} source={source}>{text}</KpiPopover>,
    tooltipStyle: HOME_KPI_TOOLTIP_STYLE,
    tooltipPlacement: "viewport-clamped",
  });

  return (
    <div data-testid="data-coverage-body">
      <KpiBand testId="data-coverage-kpis" ariaLabel="Report metrics">
        <KpiTile testId="kpi-feeds" label="Feeds" value={fmtCount(k.feeds)} sub="tracked data types" height={KPI_TILE_ROW_HEIGHT}
          {...explain("Tracked data types in this report, not the number of offices or individual source connections.")} />
        <KpiTile testId="kpi-current" label="Current" value={fmtCount(k.current)} sub="within cadence" color={T.ragGreenText} height={KPI_TILE_ROW_HEIGHT}
          {...explain("Feeds whose returned status is Current under the existing feed-specific cadence and evidence checks. Configuration alone does not verify ingestion, and Current does not guarantee every office-day is observed.")} />
        <KpiTile testId="kpi-issues" label="Partial / Issues" value={fmtCount(partialIssues)} sub="partial, stale or error" color={partialIssues > 0 ? T.ragAmberText : T.dark} height={KPI_TILE_ROW_HEIGHT}
          {...explain("All feeds with a non-Current status: Partial, Stale or Error. Includes incomplete coverage, unverified ingestion telemetry or other evidence limitations; not just failed runs or coverage below 100%.")} />
        <KpiTile testId="kpi-badging" label="Badging coverage"
          value={badgingNull ? "Unavailable" : fmtRatio(k.badgingCoverageRatio)}
          sub={`${fmtCount(p.observedDays)} / ${fmtCount(p.eligibleDays)} office-days · ${fmtMonth(selected)}`}
          {...explain(`${fmtCount(p.observedDays)} observed / ${fmtCount(p.eligibleDays)} eligible office-days; ${periodNote(d.periodState, p.isComplete)}. Eligible days exclude current/future days, weekends, holidays, days outside office lifecycle and days without positive assigned headcount. Observed zero counts as reported; missing observations are not zero. No eligible days means unavailable.`, "Canonical badging coverage")}
          height={KPI_TILE_ROW_HEIGHT}
          color={badgingNull ? T.mid : k.badgingCoverageRatio >= threshold ? T.ragGreenText : T.ragAmberText} />
        <KpiTile testId="kpi-gap-offices" label="Offices with gaps" value={fmtCount(Array.isArray(d.gaps) ? d.gaps.length : null)}
          sub={fmtMonth(selected)} height={KPI_TILE_ROW_HEIGHT}
          {...explain("Offices in the selected month's returned gaps population: at least one eligible office-day has no observation. This counts offices, not missing days or feed errors.", "Canonical badging coverage")} />
        <KpiTile testId="kpi-missing-days" label="Missing office-days" value={fmtCount(p.missingDays)}
          sub={fmtMonth(selected)} height={KPI_TILE_ROW_HEIGHT}
          {...explain("Selected-month portfolio missingDays: eligible office-days without an observation, summed across offices. Excluded days are not missing. A recorded zero attendance is observed, not missing.", "Canonical badging coverage")} />
      </KpiBand>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, margin: "16px 0" }}>
        <ViewToggle view={view} onChange={(v) => { setView(v); setDrill(null); }} />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: T.fsSm, color: T.mid }}>
          Month
          <input type="month" className="data-coverage-month" data-testid="select-month" value={selected ?? ""} min={MIN_MONTH} max={maxMonth ?? undefined}
            onChange={(e) => selectMonth(e.target.value)}
            style={{ fontSize: T.fsSm5, padding: "4px 8px", borderRadius: 7, border: `1px solid ${T.borderDark}`, background: T.card, color: T.dark }} />
        </label>
        <span data-testid="text-as-of" style={{ marginLeft: "auto", fontSize: T.fsXs5, color: T.light }}>
          As of {fmtDateTime(d.asOf) ?? "unavailable"} · days before {fmtDate(d.cutoff) ?? "unavailable"} · {d.periodState} period
        </span>
      </div>

      {drill ? (
        <OfficeDayDrilldown month={selected} office={drill} onReturn={() => setDrill(null)}
          returnLabel={view === "feed" ? "feed coverage" : "office coverage"} />
      ) : view === "feed" ? (
        <>
          <section aria-labelledby="dc-feeds" style={cardStyle}>
            <SectionHead id="dc-feeds" title="Feeds by data type" right={`${fmtCount(d.feeds?.length ?? 0)} feeds`} />
            <FeedTable key={selected} feeds={d.feeds} asOf={d.asOf} />
            <p style={{ margin: "12px 16px 16px", padding: "10px 12px", background: T.hoverBg, borderRadius: 9, color: T.mid, fontSize: T.fsSm }}>
              Ingestion times are the recorded successful run and attempt. Evidence dates (source-effective, quote, manual edit) describe the data itself, not when it was ingested.
            </p>
          </section>
          <DeepDive d={d} threshold={threshold} selected={selected} onSelectMonth={selectMonth} onOpen={setDrill} />
        </>
      ) : (
        <>
          <section aria-labelledby="dc-matrix" style={cardStyle}>
            <SectionHead id="dc-matrix" title="Office coverage matrix" right="Worst coverage first" />
            <OfficeMatrix offices={d.offices} threshold={threshold} onOpen={setDrill} periodState={d.periodState} />
          </section>
        </>
      )}
    </div>
  );
}

export function DataCoverageReport({ currentRole, initialMonth, ...shellProps }) {
  const ctx = useFeatureFlagContext() ?? {};
  const flagEnabled = (ctx.reportFlags ?? []).some((f) => f.key === REPORT_KEY && f.enabled);
  const allowed = flagEnabled && getReportNavEligibility(ctx, currentRole).dataCoverage === true;

  let content;
  if (ctx.flagsReady !== true) {
    content = <Notice testId="data-coverage-access-loading">Checking access to Data coverage…</Notice>;
  } else if (!allowed) {
    content = <Notice testId="data-coverage-access-denied" role="alert">Data coverage is available to admins and editors when the report is enabled.</Notice>;
  } else {
    content = <CoverageBody initialMonth={initialMonth} />;
  }
  return (
    <ReportShell {...shellProps} recordNavigation>
      <div data-testid="data-coverage-scroll" tabIndex={0} role="region"
        aria-label="Data coverage report; scroll horizontally on narrow screens"
        style={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto" }}>
        <div style={body}>{content}</div>
      </div>
    </ReportShell>
  );
}
